/**
 * Browser-side Web Push Infrastructure Utilities
 */
import axiosApi from '../apiServices/api/axiosAPI';
import { apiConstants } from '../apiServices/constants/apiConstants';

/**
 * Checks whether the current browser supports Web Push and Service Workers.
 * @returns {boolean}
 */
export function isPushSupported() {
    return (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
}

/**
 * Converts a URL-safe Base64 string to a Uint8Array for PushManager subscription.
 * @param {string} base64String 
 * @returns {Uint8Array}
 */
export function urlBase64ToUint8Array(base64String) {
    if (!base64String || typeof base64String !== 'string') {
        throw new Error('VAPID public key must be a valid, non-empty string.');
    }
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Converts an ArrayBuffer to a URL-safe Base64 string without padding.
 * @param {ArrayBuffer} buffer 
 * @returns {string}
 */
export function arrayBufferToBase64Url(buffer) {
    if (!buffer) return '';
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

/**
 * Extracts URL-safe Base64 p256dh and auth keys from a browser PushSubscription.
 * @param {PushSubscription} subscription 
 * @returns {{ p256dh: string, auth: string }}
 */
export function extractSubscriptionKeys(subscription) {
    if (!subscription) {
        return { p256dh: '', auth: '' };
    }

    // Modern browsers provide pre-encoded base64url keys via subscription.toJSON()
    const json = typeof subscription.toJSON === 'function' ? subscription.toJSON() : null;
    let p256dh = json?.keys?.p256dh || '';
    let auth = json?.keys?.auth || '';

    // Fallback to arrayBufferToBase64Url on raw ArrayBuffers if toJSON keys are empty
    if (!p256dh && typeof subscription.getKey === 'function') {
        const rawP256dh = subscription.getKey('p256dh');
        if (rawP256dh) {
            p256dh = arrayBufferToBase64Url(rawP256dh);
        }
    }
    if (!auth && typeof subscription.getKey === 'function') {
        const rawAuth = subscription.getKey('auth');
        if (rawAuth) {
            auth = arrayBufferToBase64Url(rawAuth);
        }
    }

    return { p256dh, auth };
}

/**
 * Generates a human-readable device label (max 64 chars) based on client user agent.
 * @returns {string}
 */
export function getDeviceLabel() {
    if (typeof navigator === 'undefined') return 'Unknown Device';

    const ua = navigator.userAgent || '';
    let os = 'Unknown OS';
    if (ua.indexOf('Win') !== -1) os = 'Windows';
    else if (ua.indexOf('Mac') !== -1) os = 'macOS';
    else if (ua.indexOf('Android') !== -1) os = 'Android';
    else if (ua.indexOf('like Mac') !== -1 || ua.indexOf('iPhone') !== -1 || ua.indexOf('iPad') !== -1) os = 'iOS';
    else if (ua.indexOf('Linux') !== -1) os = 'Linux';

    let browser = 'Browser';
    if (ua.indexOf('Edg/') !== -1) browser = 'Edge';
    else if (ua.indexOf('Chrome/') !== -1 && ua.indexOf('Edg/') === -1) browser = 'Chrome';
    else if (ua.indexOf('Firefox/') !== -1) browser = 'Firefox';
    else if (ua.indexOf('Safari/') !== -1 && ua.indexOf('Chrome/') === -1) browser = 'Safari';

    const label = `${os} - ${browser}`;
    return label.slice(0, 64);
}

/**
 * Returns truncated navigator.userAgent string (max 256 chars).
 * @returns {string}
 */
export function getUserAgent() {
    if (typeof navigator === 'undefined') return '';
    return (navigator.userAgent || '').slice(0, 256);
}

/**
 * Registers the Web Push Service Worker (/sw.js) respecting PUBLIC_URL.
 * @returns {Promise<ServiceWorkerRegistration>}
 */
export async function registerPushServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        throw new Error('Service Workers are not supported in this browser.');
    }

    const publicUrl = (process.env.PUBLIC_URL || '').trim().replace(/\/$/, '');
    const swPath = `${publicUrl}/sw.js`;
    const scope = publicUrl ? `${publicUrl}/` : '/';

    const registration = await navigator.serviceWorker.register(swPath, {
        scope,
        updateViaCache: 'none',
    });
    await navigator.serviceWorker.ready;
    return registration;
}

/**
 * Shows one persistent notification through the service worker.
 * On Windows this is the Edge/Chrome toast and the Notification Center entry.
 * @param {{ title?: string, body?: string, url?: string, tag?: string }} [notice]
 * @returns {Promise<void>}
 */
export async function showSystemNotification(notice = {}) {
    if (typeof window === 'undefined' || !('Notification' in window)) {
        return;
    }
    if (Notification.permission !== 'granted') {
        return;
    }

    const registration = await registerPushServiceWorker();
    const publicUrl = (process.env.PUBLIC_URL || '').trim().replace(/\/$/, '');
    const origin = window.location.origin;
    const icon = `${origin}${publicUrl}/logo192.png`;
    const badge = `${origin}${publicUrl}/favicon.ico`;
    const title = notice.title || 'Acme Health';
    const options = {
        body: notice.body || 'You have a new notification.',
        icon,
        badge,
        tag: notice.tag || `pmr-${Date.now()}`,
        renotify: true,
        silent: false,
        timestamp: Date.now(),
        requireInteraction: true,
        data: { url: notice.url || `${origin}${publicUrl || ''}/` },
    };

    const worker = registration.active;
    if (worker) {
        const requestId = `pmr-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const shownByWorker = await new Promise((resolve) => {
            const timer = setTimeout(() => {
                navigator.serviceWorker.removeEventListener('message', onMessage);
                resolve(false);
            }, 4000);

            function onMessage(event) {
                const data = event.data || {};
                if (data.requestId !== requestId) return;
                if (data.type !== 'NOTIFICATION_SHOWN' && data.type !== 'NOTIFICATION_FAILED') return;
                clearTimeout(timer);
                navigator.serviceWorker.removeEventListener('message', onMessage);
                resolve(data.type === 'NOTIFICATION_SHOWN');
            }

            navigator.serviceWorker.addEventListener('message', onMessage);
            worker.postMessage({ type: 'SHOW_NOTIFICATION', requestId, title, options });
        });

        if (shownByWorker) return;
    }

    try {
        await registration.showNotification(title, options);
    } catch (err) {
        const fallback = { ...options };
        delete fallback.icon;
        delete fallback.badge;
        await registration.showNotification(title, fallback);
    }
}

/**
 * Requests or checks browser notification permission.
 * Does not repeatedly prompt if already denied or granted.
 * @returns {Promise<'granted' | 'denied' | 'default' | 'unsupported'>}
 */
export async function requestNotificationPermission() {
    if (typeof window === 'undefined' || !('Notification' in window)) {
        return 'unsupported';
    }

    if (Notification.permission === 'granted') {
        return 'granted';
    }

    if (Notification.permission === 'denied') {
        return 'denied';
    }

    return await Notification.requestPermission();
}

/**
 * Shows a system toast only when GetNotificationPreference currently returns Allow (1).
 * A missing row, Block (2), or a failed lookup does not show a toast.
 * @param {{ userId: number|string, token?: string, title?: string, body?: string, url?: string, tag?: string }} notice
 */
export async function showNotificationIfAllowed(notice = {}) {
    const userId = notice.userId;
    const token = notice.token;
    if (!userId || !token) {
        return;
    }
    if (typeof window === 'undefined' || !('Notification' in window) || Notification.permission !== 'granted') {
        return;
    }

    try {
        const resp = await axiosApi.get(
            `${apiConstants.notifications.getNotificationPreference}?user_id=${userId}`,
            {
                headers: { Authorization: `Bearer ${token}` },
                skipSessionExpired: true,
            }
        );
        const preference = resp?.data?.preference_type_id ?? resp?.data?.PreferenceTypeId;
        if (Number(preference) !== 1) {
            return;
        }
        await showSystemNotification({
            title: notice.title,
            body: notice.body,
            url: notice.url,
            tag: notice.tag,
        });
    } catch (e) {
        // Missing auth or a failed lookup means the preference could not be confirmed.
    }
}

/**
 * Checks for an existing PushSubscription on the active ServiceWorkerRegistration.
 * @param {ServiceWorkerRegistration} registration 
 * @returns {Promise<PushSubscription|null>}
 */
export async function getExistingPushSubscription(registration) {
    if (!registration || !registration.pushManager) {
        return null;
    }
    return await registration.pushManager.getSubscription();
}

/**
 * Creates a new PushSubscription using the application's VAPID public key.
 * @param {ServiceWorkerRegistration} registration 
 * @returns {Promise<PushSubscription>}
 */
export async function subscribeUserToPush(registration) {
    if (!registration || !registration.pushManager) {
        throw new Error('PushManager is not supported on this browser/registration.');
    }

    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidKey || typeof vapidKey !== 'string' || !vapidKey.trim()) {
        throw new Error('VAPID public key is missing or invalid in REACT_APP_VAPID_PUBLIC_KEY.');
    }

    const convertedKey = urlBase64ToUint8Array(vapidKey.trim());

    return await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey,
    });
}

/**
 * Orchestrates the full Phase 2B browser-side subscription preparation:
 * 1. Registers /sw.js
 * 2. Reuses existing subscription if available, or creates a new one
 * 3. Extracts keys and builds backend payload
 * 
 * @param {number|string} userId 
 * @returns {Promise<{
 *   user_id: number|string,
 *   endpoint: string,
 *   p256dh: string,
 *   auth: string,
 *   user_agent: string,
 *   device_label: string
 * }>}
 */
export async function preparePushSubscriptionPayload(userId) {
    const registration = await registerPushServiceWorker();

    let subscription = await getExistingPushSubscription(registration);

    if (!subscription) {
        subscription = await subscribeUserToPush(registration);
    }

    if (!subscription || !subscription.endpoint) {
        throw new Error('Failed to obtain a valid PushSubscription from the browser.');
    }

    const { p256dh, auth } = extractSubscriptionKeys(subscription);

    return {
        user_id: userId,
        endpoint: subscription.endpoint.slice(0, 512),
        p256dh: p256dh.slice(0, 256),
        auth: auth.slice(0, 128),
        user_agent: getUserAgent(),
        device_label: getDeviceLabel(),
    };
}
